import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Shield, Target, Users, Globe, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { TopBanner } from '@/components/TopBanner';
import { GateFAQSection } from '@/components/GateFAQSection';
import { Footer } from '@/components/Footer';
import { aboutPageAIO } from '@/data/aboutPageAIO';

export const AboutPage = () => {
  return (
    <div className="min-h-screen relative">
      <GridBackground />
      <TopBanner />

      <main className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Button asChild variant="outline" className="mb-8 glass border-primary/30">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          {/* New "Knowledge Hub" Identity Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 space-y-6 mb-8 relative overflow-hidden"
          >
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />

            <div className="relative z-10 flex items-start gap-5">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20 shadow-lg shadow-primary/10">
                  <BookOpen className="w-7 h-7 text-primary" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-4xl font-bold text-white">About XtreamGate</h1>
                  <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs font-mono text-muted-foreground">Est. 2020</span>
                </div>
                <p className="text-xl text-white/80 leading-relaxed font-light mb-6">
                  The internet's premier <strong>Knowledge Hub</strong> for IPTV enthusiasts, cord-cutters, and digital streaming privacy.
                </p>
                <div className="grid md:grid-cols-3 gap-6 pt-4">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                    <Shield className="w-6 h-6 text-green-400 mb-2" />
                    <h3 className="font-bold text-sm mb-1">Safety First</h3>
                    <p className="text-xs text-muted-foreground">Dedicated to verifying domain authenticity and user privacy.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                    <Target className="w-6 h-6 text-blue-400 mb-2" />
                    <h3 className="font-bold text-sm mb-1">Objective Reviews</h3>
                    <p className="text-xs text-muted-foreground">Unbiased benchmarks of top providers and hardware.</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                    <Globe className="w-6 h-6 text-purple-400 mb-2" />
                    <h3 className="font-bold text-sm mb-1">Global Reach</h3>
                    <p className="text-xs text-muted-foreground">Guides tailored for 150+ regions and ISP environments.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mission Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass rounded-2xl p-8 md:p-12 space-y-6 mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white">Our Mission</h2>
            </div>
            <div className="prose prose-invert max-w-none text-white/70 leading-relaxed space-y-4">
              <p>
                Founded in 2020, <strong>XtreamGate</strong> was built to solve a single problem: <em>The lack of reliable information in the IPTV space.</em>
              </p>
              <p>
                With thousands of providers, endless reseller clones, and confusing setup protocols, users needed a compass. XtreamGate was established as that neutral ground—a <strong>safe harbor</strong> where users can find verified setup guides, check server status, and access official documentation for the world's leading services.
              </p>
              <p>
                We are not just a blog; we are a dedicated team of network engineers and streaming enthusiasts committed to helping you reclaim your digital freedom.
              </p>
            </div>
          </motion.div>

          {/* Verification & Authenticity Section - REBRANDED for Knowledge Hub */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="glass rounded-2xl p-8 md:p-12 space-y-6 mb-8 bg-blue-900/10 border border-blue-500/20"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-4 text-blue-300">Why Trust XtreamGate?</h3>
                <div className="space-y-4 text-white/70 leading-relaxed">
                  <p>
                    <strong className="text-white">Independent Verification:</strong> Every provider listed on our "Trusted Providers" index undergoes a rigorous 40-point stress test, including buffer analysis, VPN compatibility checks, and payment security auditing.
                  </p>
                  <p>
                    <strong className="text-white">Community Driven:</strong> We aggregate real-time feedback from our 27,000+ monthly readers to flag issues before they affect you.
                  </p>
                  <p>
                    <strong className="text-white">Technical Authority:</strong> Our guides are written by certified network administrators. We don't just tell you <em>what</em> to install; we explain <em>how</em> it works so you can troubleshoot like a pro.
                  </p>
                  <p className="pt-4 text-sm text-white/50">
                    © 2020-2025 XtreamGate. The Independent Voice of Streaming.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </main>

      <Footer />
    </div>
  );
};